// 207 §1.3 (extract, rule 3) — THE COLLECTIVE-CASE CLIENT IMPORTER'S PURE HALF.
//
// MOVED, NOT CHANGED, from `ClientImportModal` (src/views/CollectiveCaseView.tsx)
// so a recording can run the importer the browser runs: the column map, the row
// parse, the formula releases (164 §A.3) and the add / replace PLAN — what the
// import will write and link, and whom a Replace run would leave unlinked. The
// statement blocks keep the indentation they had inside `doImportInner`, which
// is how the move is checked (the commit carries each block's sha256 on both
// sides). What stayed in the component: the file reading, the React state, the
// server compute call, the vocabulary line, the duplicate report, the summary
// objects, `commit()` and the archive question.
//
// THE SEAMS ARE THE ONLY NEW CODE — four signatures, their parameter bags and
// the two return objects — and the inherited warnings moved with the blocks: the
// eslint and `any` ratchets are re-recorded as a TRANSFER (190 C.4's method), the
// view falling by what this file carries.
import { findHeaderCol as findC } from "./xlsxHeaders"; // A15, brief 65
import { parseBoolCell, parseDateCell } from "./importCells.mjs";
import { parseMoneyCell, parseComptaCell } from "./retainerImport.mjs";
import type { makeImportCell } from "./importCell";
// 211 Part Eight [RULED 24] — a member's numbers and own quantum in THIS case (the link's, never the client's)
import { memberNumbers, memberQuantum, ownQuantum, parseNumbers, withMemberNumbers, withNumbers, withOwnQuantum } from "./memberRefs.mjs";

// The seams' own types — what the moved blocks read off each value, and no
// `any` (a new file adds none: the ratchets). `rows` are sheet_to_json objects,
// `hdrs` their keys, the header rounds as the modal asserts
// `parseRetainerHeader`'s answer; the pool and the rosters are read by `id` /
// `clId` and nothing else outside the blocks' own annotations.
type SkipRow = { rn: number | string; name: string; reason: string };
/** 207 §4 — a cell imported EMPTY because it could not be read, for the summary. */
export interface CellToCheck { rn: number; name: string; column: string; raw: string }
type HeaderRound = {
  roundNumber: number;
  label: string;
  dueCol: number | null;
  paidCol: number | null;
  comptaCol: number | null;
};
type ImportCell = ReturnType<typeof makeImportCell>;

/* 164 §E (eslint ratchet) — the import's row model, typed so the retainer
   round-trip machinery (§A.1-§A.6) does not add unsafe-access debt. */
type ImportedPaymentRow = {
  roundNumber: number;
  amount: number;
  date: string;
  reference: string;
};
type ImportParsedRow = {
  rn: number;
  data: Record<string, unknown> & { quantum: number | null };
  status: string;
  importedPayments: ImportedPaymentRow[];
  dueByRound: Record<string, number>;
  linkDates: Record<string, string>;
  sheetId: number | null;
  dueReleases?: string[];
  // 207 §4 — the row's unreadable cells: "quantum" | "due:<n>" | "paid:<n>" → the file's raw text.
  marks: Record<string, string>;
  // 211 §18 — the row's contract numbers (its cell split on « ; »), for its member in THIS case
  numbers: string[];
  // 214 addendum 4 §25 — the record keys this row does NOT state (a column the sheet lacks, `notes`, an empty Type
  // cell): a merge into a stored record leaves them as they are (§24's rule on the record); a new client takes `data`.
  absentKeys?: string[];
};
type ImportLinkRow = {
  clId: number;
  status?: string;
  payments?: ImportedPaymentRow[];
  dueByRound?: Record<string, number>;
  // 213B §22 [RULED 50] — why a round's due was adapted, per round; it follows its amount (`mergeNotes`).
  dueNotes?: Record<string, string>;
  joinedDate?: string;
  suspendedDate?: string;
  terminatedDate?: string;
  // 207 §4 — beside the values, never in them: see `mergeImportMarks`.
  importMarks?: Record<string, string>;
  // 211 Part Eight — the member's numbers and own quantum in this case (src/lib/memberRefs.mjs)
  contractNumbers?: string[];
  quantum?: number;
};

export type { ImportedPaymentRow, ImportParsedRow, ImportLinkRow };


export const dupKey = (c: any) =>
    (c.type || "individual") === "company"
      ? "co::" + (c.companyName || "").trim().toLowerCase()
      : "in::" +
        (c.lastName || "").trim().toLowerCase() +
        "||" +
        (c.firstName || "").trim().toLowerCase();
export const nameOf = (c: any) =>
    c.companyName ||
    [c.firstName, c.lastName].filter(Boolean).join(" ") ||
    "(no name)";

/** The sheet's columns, by the header names it may carry (`h` is the raw header row). */
export function importColumns(h: Parameters<typeof findC>[0]) {
    return {
      // 164 §A.5(a) — the export writes the client id; a row that carries
      // one IS that client, and name pairing is only for files without it.
      id: findC(h, "Id", "id"),
      fn: findC(
        h,
        "FirstName",
        "firstname",
        "first name",
        "Prénom",
      ),
      ln: findC(h, "LastName", "lastname", "last name", "Nom"),
      nm: findC(h, "Name", "name", "Nom complet"),
      em: findC(h, "Email", "email", "E-mail"),
      ph: findC(h, "Phone", "phone", "Téléphone"),
      st: findC(h, "Street", "street", "Rue"),
      pc: findC(h, "PostalCode", "postalcode", "postal code", "CP"),
      ct: findC(h, "City", "city", "Ville"),
      co: findC(h, "Country", "country", "Pays"),
      vt: findC(h, "VAT", "vat", "TVA"),
      nr: findC(
        h,
        "National Register",
        "nationalregister",
        "national register",
        "Registre",
      ),
      lg: findC(
        h,
        "Language",
        "language",
        "Lang",
        "lang",
        "Langue",
      ),
      stt: findC(h, "Status", "status", "Statut"),
      tp: findC(h, "Type", "type"),
      cn: findC(
        h,
        "CompanyName",
        "companyname",
        "company name",
        "Société",
      ),
      tt: findC(h, "Title", "title", "Titre"),
      q: findC(h, "Quantum", "quantum"),
      // LEGAL-LANE (fix 3) — the roster row's contract number.
      // 211 §18 [RULED 24] — a member's numbers are its case's: the
      // cell (« ; » between two) goes to the member's list in THIS
      // case (the link's `contractNumbers`), never to the client's
      // record, in any mode (addendum 3 §18).
      ctr: findC(
        h,
        "N° Contrat",
        "N°Contrat",
        "No Contrat",
        "Contrat",
        "ContractNumber",
        "contractnumber",
        "Contract Number",
        "Contractnummer",
      ),
      // Phase 43 (item 4) — archived-client import columns.
      cs: findC(h, "Case", "case", "Dossier", "Affaire"),
      ar: findC(h, "Archived", "archived", "Archivé", "Gearchiveerd"),
      ad: findC(
        h,
        "Archived Date",
        "archiveddate",
        "archived date",
        "Date d'archivage",
        "Archiveringsdatum",
      ),
      // Phase 43 CORRECTION (item 3) — per-member lifecycle date
      // columns feeding the clientLink joined/suspended/terminated
      // slots. Ingested from the source (never today).
      jd: findC(h, "JoinedDate", "joineddate", "joined date", "Date d'entrée", "Toetredingsdatum"),
      sd: findC(h, "SuspendedDate", "suspendeddate", "suspended date", "Date de suspension", "Schorsingsdatum"),
      td: findC(h, "TerminatedDate", "terminateddate", "terminated date", "Date de fin", "Beëindigingsdatum"),
    };
}

/** The file's rows as the import reads them: `skipped` receives every refused row. */
export function parseImportRows({
  rows,
  hdrs,
  idx,
  applyRetainer,
  hdrRounds,
  clTyped,
  cell,
  _vTitle,
  _vCountry,
  _vClientType,
  today,
  skipped,
  hdrNames,
  checks,
}: {
  rows: Record<string, unknown>[];
  hdrs: string[];
  idx: ReturnType<typeof importColumns>;
  applyRetainer: boolean;
  hdrRounds: HeaderRound[];
  clTyped: { id: number }[];
  cell: ImportCell;
  _vTitle: Parameters<ImportCell>[1];
  _vCountry: Parameters<ImportCell>[1];
  // 214 addendum 4 §25 — the client types, so the Type cell resolves to a code instead of being coerced
  _vClientType: Parameters<ImportCell>[1];
  today: string;
  skipped: SkipRow[];
  hdrNames?: string[];
  checks?: CellToCheck[];
}): ImportParsedRow[] {
    const parsedRows: ImportParsedRow[] = [];
    rows.forEach((row, i) => {
      const rn = i + 2;
      // Cells by INDEX through the de-duplicated keys — `hdrs`, not
      // `h`: row["Compta"] would hand back the FIRST Compta for all
      // three rounds.
      const vals = hdrs.map((k: any) => row[k]);
      const fn = String(vals[idx.fn] || "").trim();
      const ln = String(vals[idx.ln] || "").trim();
      const nm = String(vals[idx.nm] || "").trim();
      const cn = String(vals[idx.cn] || "").trim();
      const tp =
        String(vals[idx.tp] || "individual")
          .trim()
          .toLowerCase() || "individual";
      let firstName = fn,
        lastName = ln,
        companyName = cn;
      if (tp === "company") {
        if (!companyName && nm) companyName = nm;
        if (!companyName) {
          skipped.push({
            rn,
            name: nm || fn || ln || "(row " + rn + ")",
            reason: "Missing CompanyName (Type=company)",
          });
          return;
        }
      } else {
        if (!firstName && !lastName && nm) {
          const parts = nm.split(/\s+/);
          firstName = parts[0] || "";
          lastName = parts.slice(1).join(" ") || "";
        }
        if (!lastName && !firstName) {
          skipped.push({
            rn,
            name: nm || cn || "(row " + rn + ")",
            reason: "Missing FirstName and LastName",
          });
          return;
        }
      }
      /* 207 §4 [RULED 2026-09-23, ruling 1] — AN UNREADABLE CELL NEVER DROPS
         THE ROW. It is stored exactly as an EMPTY cell stores (no Quantum, no
         due for that round, no payment row), and its raw text is kept as a
         MARK beside the value — the link's `importMarks`, which no calculation
         reads — so the list shows a `?` in that cell until someone checks it.
         Until this, one such cell refused the whole row, client and payments
         together (« Invalid Quantum value », « Unreadable amount »). A cell of
         nothing but spaces is empty, as parseMoneyCell has always read one. */
      const marks: Record<string, string> = {};
      const markCell = (cellKey: string, col: number | null) => {
        if (col == null) return;
        const raw = String(vals[col]);
        marks[cellKey] = raw;
        if (checks)
          checks.push({
            rn,
            name: String(nameOf({ firstName, lastName, companyName })),
            column: (hdrNames ?? hdrs)[col] ?? "",
            raw,
          });
      };
      const qRaw = vals[idx.q];
      const qBlank = typeof qRaw === "string" && qRaw.trim() === "";
      let quantum = null;
      if (qRaw !== "" && qRaw != null && !qBlank) {
        const q2 = parseFloat(
          String(qRaw).replace(/\s/g, "").replace(",", "."),
        );
        if (isNaN(q2)) markCell("quantum", idx.q);
        else quantum = q2;
      }
      const status =
        String(vals[idx.stt] || "Active").trim() || "Active";
      /* 214 addendum 4 §25 [ruling 62 (b)] — THE TYPE CELL THROUGH ITS VOCABULARY, never coerced: a code the list
         knows; else the cell's own value written through unchanged (05n's rule — a stored « natural » stays
         « natural » on a re-import); an empty cell « individual » for a NEW client and nothing for a stored one (the
         key is absent below). Until this every value but « company » became « individual », on every merge —
         staging's client 1 on 04/10 (docs/214-deploy-note.md §7.27 (4)). The company/individual branching above
         keeps reading the raw cell, as before. */
      const typeCellRaw: unknown = vals[idx.tp];
      const typeRaw = typeof typeCellRaw === "string" ? typeCellRaw.trim() : "";
      let typeCode = tp === "company" ? "company" : "individual";
      if (typeRaw) {
        const resolved: unknown = cell("client_type", _vClientType, vals[idx.tp], rn);
        if (typeof resolved === "string" && resolved) typeCode = /^(company|individual)$/i.test(resolved) ? resolved.toLowerCase() : resolved;
      }
      const data = {
        type: typeCode,
        title: cell("person_title", _vTitle, vals[idx.tt], rn),
        firstName,
        lastName,
        email: String(vals[idx.em] || "").trim(),
        phone: String(vals[idx.ph] || "").trim(),
        street: String(vals[idx.st] || "").trim(),
        postalCode: String(vals[idx.pc] || "").trim(),
        city: String(vals[idx.ct] || "").trim(),
        // 05n — was `|| "Belgium"`. See the App-side importer for the count.
        country: cell("country", _vCountry, vals[idx.co], rn),
        nationalRegisterId: String(vals[idx.nr] || "").trim(),
        quantum,
        companyName,
        vat: String(vals[idx.vt] || "").trim(),
        notes: "", // no column is ever read: a NEW client's empty note; never written over a stored one (`absentKeys`)
        /* 208 §11 — THE FILE'S LANGUAGE, WHEN ITS CELL STATES ONE. `data` is
           spread over the existing client in overwrite / replace mode, and the
           « fr » an empty cell used to give wrote over the language the firm
           holds (207C C5). 209 §5 [RULED 18] — AND A NEW CLIENT FROM AN EMPTY
           CELL HAS NO LANGUAGE: « no language is ever assumed » (208 §11 started
           it in French); its texts read its case's. Conditional, as
           `contractNumber` below, and in its place: a file that states every
           language plans byte for byte as before. */
        ...(String(vals[idx.lg] ?? "").trim()
          ? { lang: String(vals[idx.lg]).trim() }
          : {}),
        // Phase 43 (item 4) — archived-client import fields.
        formerCaseRef: String(vals[idx.cs] || "").trim(),
        archived: parseBoolCell(vals[idx.ar]),
        archivedAt: parseDateCell(vals[idx.ad]),
        /* 211 §18 [RULED 24] — "N° Contrat" is no longer a field of the
           client: `numbers` below, the member's in this case. */
      };
      /* LEGAL-LANE (fix 3) — per client, per round: due, paid and
         the accounting reference, all read positionally.
         · paid   → a payments[] row, exactly as the payment dialog
                    writes them, so the panels need no new reader.
         · compta → that payment's `reference` (displayed at :8192).
                    It replaces the old literal "Imported", which
                    threw the firm's accounting ref away.
         · due    → dueByRound[n], the stated per-member amount.
                    It cannot be a payment and it cannot be a
                    formula: this file states a due PER CLIENT, and
                    no single formula reproduces individually
                    negotiated amounts. See the dueFor widening in
                    server/src/lib/retainerRounds.js.
         A cell whose number cannot be read UNAMBIGUOUSLY is left EMPTY
         and MARKED — the row imports; money is still never guessed at
         (RULINGS 2026-09-23, ruling 1 — 207 §4; it used to refuse the
         whole row and say why). */
      const importedPayments: ImportedPaymentRow[] = [];
      const dueByRound: Record<string, number> = {};
      // 164 §A.1 — box unticked ⇒ the money cells are not even read, so an
      // ambiguous amount cannot skip a row the importer was told to ignore
      // money on.
      for (const hr of (applyRetainer ? hdrRounds : []) as any[]) {
        const hRound = hr as HeaderRound; // 207 §4 — the marks read it typed
        if (hr.dueCol != null) {
          const d = parseMoneyCell(vals[hr.dueCol]);
          if (d.error) markCell("due:" + String(hRound.roundNumber), hRound.dueCol);
          else if (d.value != null) dueByRound[hr.roundNumber] = d.value;
        }
        if (hr.paidCol == null) continue;
        const p = parseMoneyCell(vals[hr.paidCol]);
        if (p.error) {
          markCell("paid:" + String(hRound.roundNumber), hRound.paidCol);
          continue;
        }
        // Parity with the previous behaviour: only a positive paid
        // amount becomes a payment row.
        if (p.value == null || p.value <= 0) continue;
        const ref =
          hr.comptaCol != null
            ? parseComptaCell(vals[hr.comptaCol], parseDateCell)
            : "";
        importedPayments.push({
          roundNumber: hr.roundNumber,
          amount: p.value,
          date: today,
          reference: ref || "Imported",
        });
      }
      /* 164 §A.5(a) — resolve the row's Id, if the file carries the column
         and the cell has one. A positive integer that names a client in `cl`
         IS that client; an Id that matches nothing is skipped with its
         reason, never silently created; an empty cell falls back to name
         pairing. */
      let sheetId: number | null = null;
      if (idx.id >= 0) {
        const rawId = String(vals[idx.id] ?? "").trim();
        if (rawId !== "") {
          const nId = /^\d+$/.test(rawId) ? +rawId : NaN;
          const hit = nId > 0 ? clTyped.find((c) => c.id === nId) : null;
          if (!hit) {
            skipped.push({
              rn,
              name: nameOf({ firstName, lastName, companyName }),
              reason: "Id " + rawId + " unknown",
            });
            return;
          }
          sheetId = nId;
        }
      }
      // Phase 43 CORRECTION (item 3) — ingest per-member lifecycle
      // dates from the source columns (never today). Only slots with
      // a value are set; stored as YYYY-MM-DD to match in-app flips.
      const linkDates: Record<string, any> = {};
      const _jd = parseDateCell(vals[idx.jd]);
      const _sd = parseDateCell(vals[idx.sd]);
      const _td = parseDateCell(vals[idx.td]);
      if (_jd) linkDates.joinedDate = _jd.slice(0, 10);
      if (_sd) linkDates.suspendedDate = _sd.slice(0, 10);
      if (_td) linkDates.terminatedDate = _td.slice(0, 10);
      /* 214 addendum 4 §25 — THE KEYS THIS ROW DOES NOT STATE, for a merge into a stored record (§24's rule — « a
         column the sheet does not have leaves the stored value as it is » — applied to the clients sheet's record
         merge, 62 (a)): a column `findC` did not find, `notes` (no column is ever read — every merge emptied a
         stored note), `type` when its cell is empty. `recordFields` drops them; a new client takes `data` whole. */
      const absentKeys: string[] = [
        ...([
          ["title", idx.tt], ["email", idx.em], ["phone", idx.ph], ["street", idx.st], ["postalCode", idx.pc],
          ["city", idx.ct], ["country", idx.co], ["nationalRegisterId", idx.nr], ["vat", idx.vt],
          ["formerCaseRef", idx.cs], ["archived", idx.ar], ["archivedAt", idx.ad],
        ] as const).filter(([, i]) => i < 0).map(([k]) => k),
        "notes",
        ...(typeRaw ? [] : ["type"]),
      ];
      parsedRows.push({
        rn,
        data,
        status,
        importedPayments,
        dueByRound,
        linkDates,
        sheetId,
        marks,
        // 211 §18 — split on « ; » alone: a file's number may hold a comma
        numbers: idx.ctr >= 0 ? parseNumbers(vals[idx.ctr], { comma: false }) : [],
        absentKeys,
      });
    });
    return parsedRows;
}

/* 211 §18 [ADVISOR] — TWO ROWS OF ONE MEMBER STATING TWO QUANTUMS: NEITHER IS
   CHOSEN. Rows carrying the same Id are one member (164 §A.5(a)); when they
   state different quantums, no row's is applied — the member's stays as it was,
   the cell MARKED and listed in the summary, as 207 §4 treats a cell it cannot
   read (money is never guessed, 207 ruling 1). Rows without an Id are never one
   member (164 §A.5(b)). Changes the rows in place. */
export function holdConflictingQuantums(
  parsedRows: ImportParsedRow[],
  checks?: CellToCheck[],
  column = "Quantum",
): number {
  const byId = new Map<number, ImportParsedRow[]>();
  for (const pr of parsedRows) {
    if (pr.sheetId == null) continue;
    const arr = byId.get(pr.sheetId) ?? [];
    arr.push(pr);
    byId.set(pr.sheetId, arr);
  }
  let held = 0;
  for (const rows of byId.values()) {
    const stated = rows.filter((pr) => pr.data.quantum !== null);
    const values = [...new Set(stated.map((pr) => pr.data.quantum))];
    if (values.length < 2) continue;
    const raw = values.map((v) => String(v)).join(" / ");
    for (const pr of stated) {
      pr.data.quantum = null;
      pr.marks.quantum = raw;
      held++;
      if (checks) checks.push({ rn: pr.rn, name: String(nameOf(pr.data)), column, raw });
    }
  }
  return held;
}

/* 211 §18 — THE QUANTUM EACH MEMBER READS ONCE THE IMPORT IS WRITTEN, read off a
   plan of the same rows (planImport is pure): the row's member, then its quantum
   in this case (its own, else its client's). What the freeze-or-release test
   compares a stated due against. */
export function quantumsAfterPlan(plan: { rowClIds: { rn: number; clId: number }[]; newLinks: unknown[]; newCls: unknown[] }): Map<number, unknown> {
  const links = new Map((plan.newLinks as ImportLinkRow[]).map((lk) => [lk.clId, lk]));
  const clients = new Map((plan.newCls as { id: number; quantum?: unknown }[]).map((c) => [c.id, c]));
  const out = new Map<number, unknown>();
  for (const { rn, clId } of plan.rowClIds) out.set(rn, memberQuantum(links.get(clId), clients.get(clId)));
  return out;
}

/* 214 addendum 2 §20 [RULED 60; ADVISOR] — THE DATE EACH MEMBER'S FORMULA IS GATED BY ONCE THE IMPORT IS WRITTEN,
   read off the same dry plan as the quantum: an existing member keeps its link's `terminatedDate` whatever the sheet
   says (the plan leaves an existing link's status and dates alone), a member this import creates takes the sheet's.
   `dueFor` (server/src/lib/retainerRounds.js) generates no due for a round dated after it, so the freeze-or-release
   test must ask the formula WITH it — the very figure the summary shows and the export wrote — or every
   post-termination 0 the export wrote comes back as a stated 0 (the reproduction, docs/214-deploy-note.md §8′). */
export function terminatedDatesAfterPlan(plan: { rowClIds: { rn: number; clId: number }[]; newLinks: unknown[] }): Map<number, string | null> {
  const links = new Map((plan.newLinks as { clId: number; terminatedDate?: unknown }[]).map((lk) => [lk.clId, lk]));
  const out = new Map<number, string | null>();
  for (const { rn, clId } of plan.rowClIds) {
    const term = links.get(clId)?.terminatedDate;
    out.set(rn, typeof term === "string" && term.trim() ? term : null);
  }
  return out;
}

/** 164 §A.3 — a due equal to the round's formula (the server's `cmp`) releases. */
export function applyFormulaReleases(
  parsedRows: ImportParsedRow[],
  cmp: {
    perMember?: {
      perRound?: { roundNumber: number | null; due: number }[];
    }[];
  } | null,
) {
      parsedRows.forEach((pr, i) => {
        const per = cmp?.perMember?.[i]?.perRound || [];
        const byN = new Map(per.map((c) => [c.roundNumber, c.due]));
        pr.dueReleases = [];
        for (const n of Object.keys(pr.dueByRound)) {
          const formulaDue = byN.get(+n);
          if (
            typeof formulaDue === "number" &&
            Math.abs(pr.dueByRound[n] - formulaDue) < 0.005
          ) {
            delete pr.dueByRound[n];
            pr.dueReleases.push(n);
          }
        }
      });
}

/* 207 §4 — THE MARKS' LIFECYCLE. A mark is set by an import that cannot read
   the cell; it GOES when a later import reads that cell cleanly (an empty cell
   is read cleanly), when a value is set for that cell in the app, or when
   someone clicks the `?` to say it was checked. So an import keeps the marks
   of the cells it did NOT read — the money cells when the retainer box is
   off — and replaces the rest with its own. */
/** The cells an import reads: Quantum whenever the file has the column (it is
 *  read whatever the box says); each round's due and paid only when the box is
 *  ticked (unticked, the money cells are not read at all). */
export function importReadCells(
  idx: { q: number },
  hdrRounds: HeaderRound[],
  applyRetainer: boolean,
): string[] {
  const out: string[] = [];
  if (idx.q >= 0) out.push("quantum");
  if (applyRetainer)
    for (const hr of hdrRounds) {
      if (hr.dueCol != null) out.push("due:" + String(hr.roundNumber));
      if (hr.paidCol != null) out.push("paid:" + String(hr.roundNumber));
    }
  return out;
}

/** A member's marks after an import: the cells it read lose theirs, then its own unreadable cells mark. */
export function mergeImportMarks(
  prev: Record<string, string> | undefined,
  marks: Record<string, string>,
  readCells: string[],
): Record<string, string> | undefined {
  const kept = Object.entries(prev ?? {}).filter(([k]) => !readCells.includes(k));
  const next = { ...Object.fromEntries(kept), ...marks };
  return Object.keys(next).length ? next : undefined;
}

/** `{ importMarks }` when there is one, `{}` when not — for a link being built. */
function marksField(m: Record<string, string> | undefined) {
  return m && Object.keys(m).length ? { importMarks: m } : {};
}

/** A link carrying exactly `marks` (the key removed when there are none). */
export function withImportMarks<T extends { importMarks?: Record<string, string> }>(
  link: T,
  marks: Record<string, string> | undefined,
): T {
  const out = { ...link };
  if (marks && Object.keys(marks).length) out.importMarks = marks;
  else delete out.importMarks;
  return out;
}

/** A link with one cell's mark cleared — the `?` checked, or a value set in the app. */
export function clearImportMark<T extends { importMarks?: Record<string, string> }>(
  link: T,
  cell: string,
): T {
  if (!link.importMarks || !(cell in link.importMarks)) return link;
  return withImportMarks(
    link,
    Object.fromEntries(Object.entries(link.importMarks).filter(([k]) => k !== cell)),
  );
}

/* 211 Part Eight §18 [RULED 24] — THE ROW'S REFERENCES ON ITS MEMBER'S LINK.
   Its numbers beside those the member has in this case (an import never removes
   a number). Its quantum is what the member reads in this case: a client this
   import CREATES carries it on its record, as today; any other as this case's
   own where it differs from the client's — equal, the member reads the client's;
   a row stating none (an empty cell, one it could not read, one of two rows of a
   member stating two) leaves the member's as it was. */
function withRowRefs<L extends object>(
  link: L,
  pr: ImportParsedRow,
  client: { quantum?: unknown } | null | undefined,
  prev: { contractNumbers?: unknown; quantum?: unknown } | null | undefined,
  created: boolean,
) {
  const numbered = withMemberNumbers(link, withNumbers(memberNumbers(prev), pr.numbers));
  if (created) return withOwnQuantum(numbered, null);
  const q = pr.data.quantum;
  if (q === null) return withOwnQuantum(numbered, ownQuantum(prev));
  const held = client ? client.quantum : null;
  const cq = held === null || held === undefined || held === "" ? Number.NaN : Number(held);
  return withOwnQuantum(numbered, Number.isFinite(cq) && Math.abs(cq - q) < 0.005 ? null : q);
}

/** 211 §18 — what an import merges into an EXISTING client's record: the row's fields less its quantum, which is the case's. */
function recordFields(pr: ImportParsedRow): Record<string, unknown> {
  const out: Record<string, unknown> = { ...pr.data };
  delete out.quantum;
  // 214 addendum 4 §25 — a key the row does not state leaves the stored value (§24's rule on the record)
  const absent = pr.absentKeys ?? [];
  return absent.length ? Object.fromEntries(Object.entries(out).filter(([k]) => !absent.includes(k))) : out;
}

/** The add / replace plan: what the import writes and links, and whom it unlinks. */
export function planImport({
  mode,
  dupMode,
  parsedRows,
  skipped,
  cl,
  links,
  cc,
  ca,
  cs,
  hasMoneyCols,
  hasPaidCols,
  genId,
  readCells = [],
}: {
  mode: string;
  dupMode: string;
  parsedRows: ImportParsedRow[];
  skipped: SkipRow[];
  cl: { id: number }[];
  links: ImportLinkRow[];
  cc: unknown[];
  ca: unknown[];
  cs: { id: number };
  hasMoneyCols: boolean;
  hasPaidCols: boolean;
  genId: () => number;
  // 207 §4 — the cells this import reads (`importReadCells`): their old marks go.
  readCells?: string[];
}) {
    const clTyped = cl as { id: number }[];
    /* 164 §A.5(b) — SAME NAMES PAIR IN ORDER, NEVER COLLAPSE (RULINGS
       2026-09-03). One-candidate-per-key maps collapsed the firm's two
       Anna / Pietro / Andrea PIETROMARCHI onto one clId and orphaned the
       other. Every key now keeps ALL its candidates; a sheet row without an
       Id shifts the first unconsumed one, and the summary names every key
       that had more than one. A row matched by Id consumes its candidate
       too, so a later nameless row can never pair onto the same client. */
    const existingByKey = new Map<string, { id: number }[]>();
    clTyped.forEach((c) => {
      const k0 = dupKey(c);
      const arr0 = existingByKey.get(k0) || [];
      arr0.push(c);
      existingByKey.set(k0, arr0);
    });
    const orderMatched = new Map<string, number>();
    const noteOrderMatch = (name: string) =>
      orderMatched.set(name, (orderMatched.get(name) || 0) + 1);
    const linksByClId = new Map(links.map((lk: any) => [lk.clId, lk]));
    // 207 §1.2 — `genId` is the caller's: `tempId` (src/lib/tempId), never the
    // server's range.
    let imported = 0,
      created = 0,
      overwritten = 0,
      linkedExisting = 0,
      // 167 R14 — Id rows linked to their client under "create anyway".
      idLinked = 0,
      preservedPayments = 0,
      // 207B §2 — rows whose client was already on this case, counted as such.
      alreadyOnCase = 0;
    // 207B §2 — whom « Linked existing » linked, so the summary can name them:
    // a namesake who is not the same person is seen at once.
    const linked: { rn: number; clId: number; name: string }[] = [];
    // 207B §2 — the rows the import put on a client that already existed (a
    // member, or a client of the firm): the name-and-e-mail report leaves them out.
    const onExisting: number[] = [];
    // 164 §A.1 — what the retainer columns actually did, for the summary.
    // "Frozen" is every dueByRound key written; "released" is filled in by the
    // freeze-what-differs comparison (§A.3), 0 until then.
    let duesFrozen = 0,
      duesReleased = 0,
      paymentsWritten = 0;
    // 207 §3 — each imported row and the client it lands on: the pool's id, or
    // the create's temporary id until the server answers. `commit()` maps it to
    // the server's and the summary checks every row against the case's list.
    const rowClIds: { rn: number; clId: number }[] = [];
    /* LEGAL-LANE (fix 3) — the per-round due override rides on the
       link beside `payments`, and follows the SAME rule payments
       follow: written when the sheet states it, left alone when the
       sheet is silent. A file with no retainer columns must never
       blank a due the firm already recorded. */
    const dueOf = (pr: ImportParsedRow) =>
      hasMoneyCols && Object.keys(pr.dueByRound || {}).length > 0
        ? { dueByRound: pr.dueByRound }
        : {};
    /* 164 §A.1 — summary counters for a row whose link takes the sheet's money.
       214 addendum 3 §22 [ADVISOR] — « figé » counts a due that is NEW or whose
       AMOUNT CHANGED against the member's stored `dueByRound` (`prevDue`, the
       link the merge writes over; none for a member this import creates, so
       every stated key is new). An equal re-statement counts nowhere — the
       half-cent equality is `mergeNotes`' own, so a due counted as frozen is
       exactly one whose note goes. Before: every key written, so an export
       re-imported unchanged read « 1 dû figé » for a stated due written again
       at its own amount (docs/214-deploy-note.md §7.22). */
    const countDues = (
      pr: ImportParsedRow,
      prevDue?: Record<string, number> | null,
    ) => {
      if (!hasMoneyCols) return;
      for (const [n, amt] of Object.entries(pr.dueByRound || {})) {
        const was = (prevDue || {})[n];
        if (!(typeof was === "number" && Math.abs(amt - was) < 0.005)) duesFrozen++;
      }
    };
    /* 164 §A.3 — per-key merge of the sheet's due statements into a member's
       existing dueByRound: sheet-stated keys win (freeze), released keys go
       (a cell equal to the formula releases a previously stated due), keys
       the sheet is silent on survive (preserve-on-silence, unchanged). */
    const mergeDue = (
      pr: ImportParsedRow,
      prevDue: Record<string, number> | null | undefined,
    ) => {
      const base: Record<string, number> = { ...(prevDue || {}) };
      for (const n of pr.dueReleases || []) {
        if (base[n] !== undefined) {
          delete base[n];
          duesReleased++;
        }
      }
      if (hasMoneyCols) Object.assign(base, pr.dueByRound || {});
      return base;
    };
    /* 213B §22 [RULED 50] — A NOTE FOLLOWS ITS AMOUNT. The note a person wrote
       beside an adapted due (`dueNotes`, per round) survives an import only
       where the amount it explains survives UNCHANGED: a key the sheet releases
       loses its note, a key whose amount the sheet changes loses it, a key
       re-stated with the same amount keeps it, a key the sheet is silent on
       keeps it [ADVISOR — re-stating what stands overwrites nothing, so an
       export re-imported unchanged keeps every note]. The import writes no note
       of its own. Beside `mergeDue`, at its three sites. */
    const mergeNotes = (
      prevDue: Record<string, number> | null | undefined,
      prevNotes: Record<string, string> | null | undefined,
      nextDue: Record<string, number>,
    ): Record<string, string> | undefined => {
      const out: Record<string, string> = {};
      for (const [n, text] of Object.entries(prevNotes || {})) {
        const was = (prevDue || {})[n];
        const now = nextDue[n];
        if (typeof now === "number" && typeof was === "number" && Math.abs(now - was) < 0.005) out[n] = text;
      }
      return Object.keys(out).length ? out : undefined;
    };
    const countPayments = (pr: ImportParsedRow) => {
      if (hasPaidCols) paymentsWritten += pr.importedPayments.length;
    };
    /* 164 §A.4 — AN IMPORT NEVER REPLACES A MEMBER'S EXISTING PAYMENT ROWS
       (RULINGS 2026-09-03, the fifth line, launched standing). A sheet paid
       cell writes a payment only for a round where the member has NO payment
       row — strict === on roundNumber, the rule paidForRound follows. Real
       rows (dates, references) always survive a round trip. */
    const mergePayments = (
      pr: ImportParsedRow,
      prevPayments: ImportedPaymentRow[] | null | undefined,
    ) => {
      const prevP = Array.isArray(prevPayments) ? prevPayments : [];
      if (!hasPaidCols) return prevP;
      const added = pr.importedPayments.filter(
        (ip) => !prevP.some((p) => p.roundNumber === ip.roundNumber),
      );
      paymentsWritten += added.length;
      return added.length ? [...prevP, ...added] : prevP;
    };
    if (mode === "add") {
      const newCls = [...cl];
      const newLinks = [...links];
      // 164 §A.5(b) — clients already paired this run (by Id or by order).
      const consumedAdd = new Set<any>();
      for (const pr of parsedRows) {
        const k = dupKey(pr.data);
        // 164 §A.5(a) — a row with an Id IS that client; the name key only
        // decides for rows without one, shifting the first unconsumed
        // candidate (§A.5(b)).
        let exist: any;
        if (pr.sheetId != null) {
          exist = newCls.find((c: any) => c.id === pr.sheetId);
        } else {
          const arrK = existingByKey.get(k) || [];
          exist = arrK.find((c: any) => !consumedAdd.has(c.id));
          if (exist && arrK.length > 1) noteOrderMatch(nameOf(exist));
        }
        if (exist) consumedAdd.add(exist.id);
        if (exist) {
          if (dupMode === "skip") {
            skipped.push({
              rn: pr.rn,
              name: nameOf(pr.data),
              reason:
                'Duplicate of existing client "' +
                nameOf(exist) +
                '" (skip mode)',
            });
            continue;
          }
          /* 167 R14 — AN ID ROW IS NEVER CREATED AS A NEW CLIENT, whatever
             dupMode says. Under "new" a row whose Id names an existing
             client takes the link shape below — the sheet's dues and
             payments merged per 164 §A.3/§A.4 — with NO merge of the
             sheet's fields into the client's own record. "Overwrite" keeps
             its meaning (fields merged, then linked); a NAME-only row under
             "new" still falls through to creation, as before. */
          const idLink = dupMode === "new" && pr.sheetId != null;
          /* 207B §1 — A NAME THE FIRM ALREADY KNOWS GOES ON THE CASE. "link",
             the default: the existing client, put on this case, their record
             unchanged — the shape above, for a name row too. The file's
             status, dues, payments and marks go on the LINK; nothing is merged
             into the client's own fields. */
          const keepRecord = dupMode === "link" || idLink;
          if (dupMode === "overwrite" || keepRecord) {
            if (idLink) {
              idLinked++;
            } else if (!keepRecord) {
              const merged = { ...exist, ...recordFields(pr), id: exist.id }; // 211 §18 — never the file's quantum
              const ix = newCls.findIndex((c) => c.id === exist.id);
              if (ix >= 0) newCls[ix] = merged;
              // 164 §A.5(b) — replace the candidate inside its array.
              {
                const arrK = existingByKey.get(k) || [];
                const ai = arrK.findIndex(
                  (c: any) => c.id === exist.id,
                );
                if (ai >= 0) arrK[ai] = merged;
              }
              overwritten++;
            }
            imported++;
            rowClIds.push({ rn: pr.rn, clId: (exist as { id: number }).id });
            onExisting.push(pr.rn);
            const lix = newLinks.findIndex(
              (x) => x.clId === exist.id,
            );
            // 207B §2 — a row whose client is already on this case is counted as such.
            if (lix >= 0) alreadyOnCase++;
            // 211 §18 — the client whose quantum the member's is compared with: its record after this row
            const recNow = newCls.find((c) => c.id === exist.id) as { quantum?: unknown } | undefined;
            if (lix < 0) {
              newLinks.push(withRowRefs({
                clId: exist.id,
                status: pr.status,
                // 164 §A.1 — reads through the checkbox gate.
                payments: hasPaidCols ? pr.importedPayments : [],
                ...dueOf(pr),
                ...pr.linkDates,
                ...marksField(pr.marks),
              }, pr, recNow, undefined, false));
              countPayments(pr);
              countDues(pr);
              linksByClId.set(
                exist.id,
                newLinks[newLinks.length - 1],
              );
              linkedExisting++;
              linked.push({ rn: pr.rn, clId: (exist as { id: number }).id, name: String(nameOf(exist)) });
            } else if (
              (hasPaidCols && pr.importedPayments.length > 0) ||
              Object.keys(dueOf(pr)).length > 0 ||
              (pr.dueReleases || []).length > 0
            ) {
              // 164 §A.3 — same per-key merge as replace mode: stated keys
              // freeze, equal cells release, silent keys survive.
              const prevL = newLinks[lix] as ImportLinkRow;
              countDues(pr, prevL.dueByRound); // 214 addendum 3 §22 — against the stored dues
              const mDue = mergeDue(pr, prevL.dueByRound);
              // 164 §A.4 — payments merge, never replace (counts inside).
              const mPay = mergePayments(pr, prevL.payments);
              // 213B §22 — the note follows its amount.
              const mNotes = mergeNotes(prevL.dueByRound, prevL.dueNotes, mDue);
              const nl2: ImportLinkRow = {
                ...prevL,
                payments: mPay,
              };
              if (Object.keys(mDue).length) nl2.dueByRound = mDue;
              else delete nl2.dueByRound;
              if (mNotes) nl2.dueNotes = mNotes;
              else delete nl2.dueNotes;
              newLinks[lix] = nl2;
            }
            // 207 §4 — the marks follow the cells this import read, money or not.
            if (lix >= 0)
              newLinks[lix] = withImportMarks(
                newLinks[lix],
                mergeImportMarks(newLinks[lix].importMarks, pr.marks, readCells),
              );
            // 211 §18 — and its numbers and quantum, in every mode, money or not
            if (lix >= 0) newLinks[lix] = withRowRefs(newLinks[lix], pr, recNow, newLinks[lix], false);
            continue;
          }
        }
        const ni = { ...pr.data, id: genId() };
        newCls.push(ni);
        // 164 §A.5(b) — a created client joins its key's candidate list.
        {
          const kN = dupKey(ni);
          const arrN = existingByKey.get(kN) || [];
          arrN.push(ni);
          existingByKey.set(kN, arrN);
        }
        /* 207B §1 — under "link" a later row never pairs with a client THIS
           import created: two rows of one name are two people (§A.5(b)), and
           pairing them would put the second's dues on the first's link. The
           three other options keep the candidate list exactly as it was. */
        if (dupMode === "link") consumedAdd.add(ni.id);
        newLinks.push(withRowRefs({
          clId: ni.id,
          status: pr.status,
          // 164 §A.1 — reads through the checkbox gate.
          payments: hasPaidCols ? pr.importedPayments : [],
          ...dueOf(pr),
          ...pr.linkDates,
          ...marksField(pr.marks),
        }, pr, ni, undefined, true));
        countPayments(pr);
        countDues(pr);
        linksByClId.set(ni.id, newLinks[newLinks.length - 1]);
        created++;
        imported++;
        rowClIds.push({ rn: pr.rn, clId: ni.id });
      }
      return {
        newCls, newLinks, imported, created, overwritten, linkedExisting, idLinked,
        preservedPayments, duesFrozen, duesReleased, paymentsWritten, orderMatched,
        dropped: 0, trueOrphans: [] as number[], rowClIds, alreadyOnCase, linked, onExisting,
      };
    } else {
      // replace mode
      // 164 §A.5(b) — key → candidate[] in link order; one-per-key collapsed
      // same-name members onto one clId and orphaned the rest.
      const linkedById = new Map<
        string,
        { client: { id: number }; link: ImportLinkRow }[]
      >();
      (links as ImportLinkRow[]).forEach((lk) => {
        const c = clTyped.find((x) => x.id === lk.clId);
        if (!c) return;
        const kL = dupKey(c);
        const arrL = linkedById.get(kL) || [];
        arrL.push({ client: c, link: lk });
        linkedById.set(kL, arrL);
      });
      const consumedClIds = new Set<number>();
      const matchedOldIds = new Set();
      const newCls = [...cl];
      // 157 §2 — annotated because `finish` below now reads this from a
      // closure, and TS cannot evolve an implicit any[] across one.
      const newLinks: any[] = [];
      for (const pr of parsedRows) {
        const k = dupKey(pr.data);
        /* 211 §18 — ROWS CARRYING THE SAME ID ARE ONE MEMBER (164 §A.5(a)): a
           second such row goes on the link an earlier row of this run placed —
           its dues, payments, marks and numbers merged as Add merges them —
           never on a second link for the same client, which is what it did. */
        const placed = newLinks as ImportLinkRow[];
        const again = pr.sheetId == null ? -1 : placed.findIndex((x) => x.clId === pr.sheetId);
        if (again >= 0) {
          const cur = placed[again];
          const mDue = mergeDue(pr, cur.dueByRound);
          countDues(pr, cur.dueByRound); // 214 addendum 3 §22
          const nl: ImportLinkRow = { ...cur, payments: mergePayments(pr, cur.payments) };
          if (Object.keys(mDue).length) nl.dueByRound = mDue;
          else delete nl.dueByRound;
          // 213B §22 — the note follows its amount.
          const mNotes2 = mergeNotes(cur.dueByRound, cur.dueNotes, mDue);
          if (mNotes2) nl.dueNotes = mNotes2;
          else delete nl.dueNotes;
          newLinks[again] = withRowRefs(
            withImportMarks(nl, mergeImportMarks(cur.importMarks, pr.marks, readCells)),
            pr,
            newCls.find((c) => c.id === pr.sheetId) as { quantum?: unknown } | undefined,
            cur,
            false,
          );
          alreadyOnCase++;
          onExisting.push(pr.rn);
          imported++;
          rowClIds.push({ rn: pr.rn, clId: cur.clId });
          continue;
        }
        /* 164 §A.5(a) — id first: a row with an Id matches that client
           whether or not it is currently linked to this case. Linked ⇒ the
           prev.link merge below; UNLINKED ⇒ the Add-mode "link existing"
           shape (fresh link, merged client fields) — never a guessed pair. */
        let prev: {
          client: { id: number };
          link: ImportLinkRow;
        } | null = null;
        if (pr.sheetId != null) {
          const lk0 = linksByClId.get(pr.sheetId) as
            | ImportLinkRow
            | undefined;
          const c0 = clTyped.find((x) => x.id === pr.sheetId);
          if (lk0 && c0) {
            prev = { client: c0, link: lk0 };
          } else if (c0) {
            const merged0 = { ...c0, ...recordFields(pr), id: c0.id }; // 211 §18 — never the file's quantum
            const ix0 = newCls.findIndex((x) => x.id === c0.id);
            if (ix0 >= 0) newCls[ix0] = merged0;
            newLinks.push(withRowRefs({
              clId: c0.id,
              status: pr.status,
              payments: hasPaidCols ? pr.importedPayments : [],
              ...dueOf(pr),
              ...pr.linkDates,
              ...marksField(pr.marks),
            }, pr, merged0 as { quantum?: unknown }, undefined, false));
            countPayments(pr);
            countDues(pr);
            matchedOldIds.add(c0.id);
            consumedClIds.add(c0.id);
            linkedExisting++;
            linked.push({ rn: pr.rn, clId: c0.id, name: String(nameOf(c0)) });
            onExisting.push(pr.rn);
            imported++;
            rowClIds.push({ rn: pr.rn, clId: c0.id });
            continue;
          }
        } else {
          // 164 §A.5(b) — shift the first unconsumed candidate for this
          // name; a key with more than one candidate is reported.
          const arrK = linkedById.get(k) || [];
          const cand = arrK.find(
            (x) => !consumedClIds.has(x.client.id),
          );
          if (cand) {
            prev = cand;
            if (arrK.length > 1) noteOrderMatch(nameOf(cand.client));
          }
          /* 207B §1 — A NAME THE FIRM ALREADY KNOWS GOES ON THE CASE. No
             member of this case has the name and a client of the firm does:
             by default ("link") that client, put on this case, their record
             unchanged — Add's shape. "overwrite" updates the record from the
             file first; "skip" leaves the row out, named in the summary; "new"
             creates, as Replace always did. Same names pair in order: the
             first of the firm's candidates this run has not paired. */
          if (!cand && dupMode !== "new") {
            const arrF = existingByKey.get(k) ?? [];
            const firm = arrF.find((c) => !consumedClIds.has(c.id));
            if (firm) {
              const firmName = String(nameOf(firm));
              consumedClIds.add(firm.id);
              if (arrF.length > 1) noteOrderMatch(firmName);
              if (dupMode === "skip") {
                skipped.push({
                  rn: pr.rn,
                  name: String(nameOf(pr.data)),
                  reason: 'Duplicate of existing client "' + firmName + '" (skip mode)',
                });
                continue;
              }
              if (dupMode === "overwrite") {
                const mergedF = { ...firm, ...recordFields(pr), id: firm.id }; // 211 §18 — never the file's quantum
                const ixF = newCls.findIndex((c) => c.id === firm.id);
                if (ixF >= 0) newCls[ixF] = mergedF;
                overwritten++;
              }
              newLinks.push(withRowRefs({
                clId: firm.id,
                status: pr.status,
                payments: hasPaidCols ? pr.importedPayments : [],
                ...dueOf(pr),
                ...pr.linkDates,
                ...marksField(pr.marks),
              }, pr, firm as { quantum?: unknown }, undefined, false));
              countPayments(pr);
              countDues(pr);
              linkedExisting++;
              linked.push({ rn: pr.rn, clId: firm.id, name: firmName });
              onExisting.push(pr.rn);
              imported++;
              rowClIds.push({ rn: pr.rn, clId: firm.id });
              continue;
            }
          }
        }
        if (prev) consumedClIds.add(prev.client.id);
        if (prev) {
          const merged = {
            ...prev.client,
            ...recordFields(pr), // 211 §18 — never the file's quantum
            id: prev.client.id,
          };
          const ix = newCls.findIndex(
            (c) => c.id === prev.client.id,
          );
          if (ix >= 0) newCls[ix] = merged;
          // 164 §A.4 — was: sheet payments REPLACE the member's rows.
          // Now they merge, per round, only where the member has none.
          const finalPayments = mergePayments(pr, prev.link.payments);
          // 164 §A.3 — was a wholesale replace (sheet dues win, all
          // existing keys dropped); now the per-key merge: stated keys
          // freeze, equal cells release, silent keys survive.
          const finalDue = mergeDue(pr, prev.link.dueByRound);
          countDues(pr, prev.link.dueByRound); // 214 addendum 3 §22
          // 213B §22 — the note follows its amount.
          const finalNotes = mergeNotes(prev.link.dueByRound, prev.link.dueNotes, finalDue);
          newLinks.push(withRowRefs({
            clId: prev.client.id,
            status: prev.link.status || pr.status,
            payments: finalPayments,
            ...(finalDue && Object.keys(finalDue).length
              ? { dueByRound: finalDue }
              : {}),
            ...(finalNotes ? { dueNotes: finalNotes } : {}),
            // Phase 43 CORR item 3 — preserve the member's existing
            // lifecycle dates on re-import (parity with status/payments).
            ...(prev.link.joinedDate
              ? { joinedDate: prev.link.joinedDate }
              : {}),
            ...(prev.link.suspendedDate
              ? { suspendedDate: prev.link.suspendedDate }
              : {}),
            ...(prev.link.terminatedDate
              ? { terminatedDate: prev.link.terminatedDate }
              : {}),
            // 207 §4 — the member's marks: the cells this import read lose
            // theirs, then this row's unreadable cells mark.
            ...marksField(
              mergeImportMarks(prev.link.importMarks, pr.marks, readCells),
            ),
          }, pr, merged as { quantum?: unknown }, prev.link, false));
          matchedOldIds.add(prev.client.id);
          // 164 §A.4 — existing rows are ALWAYS preserved now; the count is
          // every matched member who had rows to preserve.
          if ((prev.link.payments || []).length > 0)
            preservedPayments++;
          // 207B §2 — a member of this case: counted as already on it.
          alreadyOnCase++;
          onExisting.push(pr.rn);
          imported++;
          rowClIds.push({ rn: pr.rn, clId: prev.client.id });
        } else {
          const ni = { ...pr.data, id: genId() };
          newCls.push(ni);
          newLinks.push(withRowRefs({
            clId: ni.id,
            status: pr.status,
            // 164 §A.1 — reads through the checkbox gate.
            payments: hasPaidCols ? pr.importedPayments : [],
            ...dueOf(pr),
            ...pr.linkDates,
            ...marksField(pr.marks),
          }, pr, ni, undefined, true));
          countPayments(pr);
          countDues(pr);
          created++;
          imported++;
          rowClIds.push({ rn: pr.rn, clId: ni.id });
        }
      }
      const droppedIds = links
        .filter((lk: any) => !matchedOldIds.has(lk.clId))
        .map((lk: any) => lk.clId);
      const dropped = droppedIds.length;
      const otherCcLinked = new Set();
      (cc || []).forEach((c2: any) => {
        if (c2.id === cs.id) return;
        (c2.clientLinks || []).forEach((lk: any) =>
          otherCcLinked.add(lk.clId),
        );
      });
      const caLinked = new Set();
      (ca || []).forEach((c2: any) =>
        (c2.clIds || []).forEach((x: any) => caLinked.add(x)),
      );
      const trueOrphans = droppedIds.filter(
        (id: any) => !otherCcLinked.has(id) && !caLinked.has(id),
      );
      return {
        newCls, newLinks, imported, created, overwritten, linkedExisting, idLinked,
        preservedPayments, duesFrozen, duesReleased, paymentsWritten, orderMatched,
        dropped, trueOrphans: trueOrphans as number[], rowClIds, alreadyOnCase, linked, onExisting,
      };
    }
}

/* 207 §3 — THE SUMMARY ADDS UP, BY CONSTRUCTION. Every data row of the file
   (Excel rows 2 … total + 1, the importer's `rn`) must be exactly one of:
   IMPORTED — and then its client is on the case's list: linked by the roster
   AND held in the client list the view joins it with (the list shows
   `clientLinks` joined with the page's clients, CollectiveCaseView's `allCls`)
   — or REFUSED, with its reason. A silent mismatch is what hid 207 §1 (the
   server's roster held 253 while the list showed 5) and §2: the summary now
   names every row that is neither, both, or counted and not listed. Pure, so
   the summary runs it on every render against the LIVE list, and the
   recording runs the same function. */
export function importOutcomeCheck({
  total,
  imported,
  refused,
  links,
  clients,
}: {
  total: number;
  imported: { rn: number; clId: number }[];
  refused: number[];
  links: { clId: number }[];
  clients: { id: number }[];
}): {
  unaccounted: number[];
  twice: number[];
  notOnCase: { rn: number; clId: number; why: "roster" | "client" }[];
} {
  const count = new Map<number, number>();
  for (const r of imported) count.set(r.rn, (count.get(r.rn) ?? 0) + 1);
  for (const rn of refused) count.set(rn, (count.get(rn) ?? 0) + 1);
  const unaccounted: number[] = [];
  const twice: number[] = [];
  for (let rn = 2; rn <= total + 1; rn++) {
    const n = count.get(rn) ?? 0;
    if (n === 0) unaccounted.push(rn);
    else if (n > 1) twice.push(rn);
  }
  const linked = new Set(links.map((l) => l.clId));
  const held = new Set(clients.map((c) => c.id));
  const notOnCase = imported
    .filter((r) => !linked.has(r.clId) || !held.has(r.clId))
    .map((r) => ({ rn: r.rn, clId: r.clId, why: linked.has(r.clId) ? ("client" as const) : ("roster" as const) }));
  return { unaccounted, twice, notOnCase };
}
